import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import Cookies from "js-cookie";

const GoogleAuthContext = createContext(null);

const GOOGLE_CLIENT_ID = "262070749684-nt66f8pap863o9hog2t6g5fctkchjlli.apps.googleusercontent.com";

export const GoogleAuthProvider = ({ children }) => {
  const [isGoogleAuthenticated, setIsGoogleAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [googleUser, setGoogleUser] = useState(null);
  const [gapiLoaded, setGapiLoaded] = useState(false);
  const [gisLoaded, setGisLoaded] = useState(false);
  const googleButtonRef = useRef(null);

  // Verificar si ya hay un token guardado en las cookies
  useEffect(() => {
    const token = Cookies.get("accessToken");
    if (token) {
      console.log("Token encontrado, verificando autenticación...");
      setIsGoogleAuthenticated(true);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, []);

  // Cargar GAPI (Google API Client) solo si no está autenticado
  useEffect(() => {
    if (isGoogleAuthenticated || gapiLoaded) return;

    const loadGapi = () => {
      console.log("Cargando script de GAPI...");
      const script = document.createElement("script");
      script.src = "https://apis.google.com/js/api.js";
      script.async = true;
      script.defer = true;
      script.onload = initializeGapi;
      script.onerror = () => {
        console.error("Error cargando la API de Google");
        setIsLoading(false);
      };
      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    };

    loadGapi();
  }, [isGoogleAuthenticated, gapiLoaded]);

  // Cargar GIS (Google Identity Services) solo si no está autenticado
  useEffect(() => {
    if (isGoogleAuthenticated || gisLoaded) return;

    const loadGis = () => {
      console.log("Cargando script de GIS...");
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("Script de GIS cargado correctamente");
        setGisLoaded(true);
      };
      script.onerror = () => {
        console.error("Error cargando la API de Google Identity Services");
        setIsLoading(false);
      };
      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    };

    loadGis();
  }, [isGoogleAuthenticated, gisLoaded]);

  // Inicializar GAPI
  const initializeGapi = () => {
    console.log("Inicializando GAPI...");
    window.gapi.load('client', async () => {
      try {
        await window.gapi.client.init({
          apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
          discoveryDocs: ["https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest"],
        });
        
        console.log("GAPI inicializado correctamente");
        setGapiLoaded(true);
        setIsLoading(false);
      } catch (error) {
        console.error("Error inicializando GAPI:", error);
        setIsLoading(false);
      }
    });
  };

  // Configurar el botón de inicio de sesión de Google
  useEffect(() => {
    if (gisLoaded && googleButtonRef.current && !isGoogleAuthenticated) {
      console.log("Configurando botón de Google...");
      try {
        const buttonContainer = document.createElement('div');
        buttonContainer.id = 'google-signin-button';
        buttonContainer.style.display = 'none';
        document.body.appendChild(buttonContainer);

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true
        });

        console.log("Cliente de GIS inicializado correctamente");
      } catch (error) {
        console.error("Error configurando el botón de Google:", error);
      }
    }
  }, [gisLoaded, googleButtonRef, isGoogleAuthenticated]);

  // Manejar la respuesta de autenticación
  const handleCredentialResponse = async (response) => {
    console.log("Respuesta de credenciales recibida");
    if (response.credential) {
      try {
        const user = parseJwt(response.credential);
        console.log("Usuario autenticado:", user);

        sessionStorage.setItem('google_token', response.credential);
        Cookies.set("accessToken", response.credential, { expires: 7 }); // Guardar el token en cookies

        setGoogleUser(user);
        setIsGoogleAuthenticated(true);

        if (gapiLoaded) {
          try {
            await window.gapi.client.setToken({
              access_token: response.credential
            });
            console.log("Token establecido en GAPI");
          } catch (error) {
            console.error("Error estableciendo el token en GAPI:", error);
          }
        }
      } catch (error) {
        console.error("Error procesando la respuesta de credenciales:", error);
      }
    }
  };

  const parseJwt = (token) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error al parsear JWT:", error);
      return {};
    }
  };

  const signIn = () => {
    if (!gisLoaded) {
      console.error("Google Identity Services no está cargado");
      return;
    }

    try {
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          console.log("El prompt no se mostró o fue omitido");
          const buttonContainer = document.getElementById('google-signin-button');
          if (buttonContainer) {
            window.google.accounts.id.renderButton(
              buttonContainer, 
              { 
                type: 'standard',
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
                shape: 'rectangular',
                width: 250
              }
            );
            const googleButton = buttonContainer.querySelector('div[role="button"]');
            if (googleButton) {
              googleButton.click();
            }
          }
        }
      });
    } catch (error) {
      console.error("Error al iniciar sesión:", error);
    }
  };

  const signOut = () => {
    console.log("Cerrando sesión...");
    try {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.disableAutoSelect();
      }

      if (gapiLoaded) {
        window.gapi.client.setToken(null);
      }

      sessionStorage.removeItem('google_token');
      Cookies.remove("accessToken"); // Eliminar el token de las cookies
      setGoogleUser(null);
      setIsGoogleAuthenticated(false);
      console.log("Sesión cerrada correctamente");
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  // Crear evento en Google Calendar
  const createCalendarEvent = async (eventData) => {
    if (!gapiLoaded) {
      throw new Error("GAPI no está cargado");
    }

    try {
      const token = Cookies.get("accessToken");
      if (!token) {
        throw new Error("No hay token de autenticación");
      }

      window.gapi.client.setToken({ access_token: token });

      const response = await window.gapi.client.calendar.events.insert({
        calendarId: "primary",
        resource: eventData,
        conferenceDataVersion: 1,
        sendUpdates: "all",
      });

      console.log("Evento creado correctamente:", response.result);
      return response.result;
    } catch (error) {
      console.error("Error creando evento en Google Calendar:", error);
      throw error;
    }
  };

  // Obtener eventos del Google Calendar
  const getCalendarEvents = async () => {
    if (!gapiLoaded) {
      throw new Error("GAPI no está cargado");
    }

    try {
      const token = Cookies.get("accessToken");
      if (!token) {
        throw new Error("No hay token de autenticación");
      }

      window.gapi.client.setToken({ access_token: token });

      const response = await window.gapi.client.calendar.events.list({
        calendarId: "primary",
        timeMin: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
        timeMax: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString(),
        showDeleted: false,
        singleEvents: true,
        maxResults: 100,
        orderBy: "startTime",
      });

      console.log("Eventos obtenidos correctamente:", response.result.items);
      return response.result.items;
    } catch (error) {
      console.error("Error obteniendo eventos de Google Calendar:", error);
      throw error;
    }
  };

  return (
    <GoogleAuthContext.Provider
      value={{
        isGoogleAuthenticated,
        isLoading,
        googleUser,
        signIn,
        signOut,
        createCalendarEvent,
        getCalendarEvents,
      }}
    >
      {children}
    </GoogleAuthContext.Provider>
  );
};

export const useGoogleAuth = () => {
  const context = useContext(GoogleAuthContext);
  if (!context) {
    throw new Error("useGoogleAuth debe ser usado dentro de GoogleAuthProvider");
  }
  return context;
};
